import { createPortal } from 'react-dom'

const FanCreatorContractModal = ({ onClose }: { onClose: () => void }) => {
  return createPortal(
    <div id="modal-example" className="pf-modal">
      <div className="pf-modal__dialog">
        <div className="pf-modal__content">
          <div className="pf-modal__body">
            <div className="pf-page__logo">
              <div className="pf-page__logo-content">
                <img
                  src='/logo.png'
                  alt='AllFans'
                  className='pf-page__logo-img'
                />
              </div>
            </div>
            <h2 className="pf-page__title">
              All<span>Fans</span>
            </h2>
            <div className="pf-page__descr">Standard Agreement Between Fan and Creator</div>
          <div className="pf-content__description">
            <p>
              This standard agreement between the Fan and the Creator sets out the terms 
              that govern each transaction between a Fan and a Creator on AllFans.
            </p>
          </div>
          <ol className="pf-main-list">
            <li>
              <h5>Definitions</h5>
              <p>In this agreement, the following definitions apply:</p>
              <p>
                <strong>“Content”</strong> means any material uploaded to the
                Website by any User (whether a Creator or a Fan), including any
                photos, videos, audio (for example, music and other sounds),
                livestream material, data, text (including comments and
                hashtags), metadata, images, interactive features, emojis, GIFs,
                memes, and any other material.
              </p>
              <p>
                <strong>“Creator”</strong> means a User who has set up their
                AllFans account as a Creator account to post Content on
                the Website to be viewed by other Users.
              </p>
              <p>
                <strong>“Fan”</strong> means a User who follows a Creator and
                can view the Creator’s Content.
              </p>
              <p>
                <strong>“Fan/Creator Transaction”</strong> means any transaction
                between a Fan and Creator on the Website by which access is
                granted to the Creator’s Content, including in any of the
                following ways: (1) a Subscription, (2) payments made by a Fan
                to view a Creator’s pay-per-view Content (pay-per-view media and
                pay-per-view live stream), and (3) use by the Fan of the fan
                interaction function on a Creator’s account.
              </p>
              <p>
                <strong>“Fan Payment”</strong> means any payments made by a Fan
                to a Creator (1) for a Fan/Creator Transaction, or (2) by way of
                a tip for a Creator.
              </p>
              <p>
                <strong>“AllFans”</strong> means the website located at <a href='/' className='terms-link'>www.allfans.com</a>.
              </p>
              <p>
                <strong>“Platform Fee”</strong> means the fee charged by Innodev Solutions 
                  Inc. to Creators in accordance with the <a href='/creatorterms' className='terms-link'>Creator Terms</a>.
              </p>
              <p>
                <strong>“Referring User”</strong> means a User who participates in the AllFans 
                  Referral Program.
              </p>
              <p>
                <strong>“Relevant Content”</strong> has the meaning set out in section 5.
              </p>
              <p>
                <strong>“Subscription”</strong> means a Fan’s subscription to a
                Creator’s account (whether paid or unpaid, and whether for one
                month or as part of a bundle comprising a subscription for more
                than one month).
              </p>
              <p>
                <strong>“Tax”</strong> means all forms of tax and statutory,
                governmental, state, federal, provincial, local government, or
                municipal charges, duties, imposts, contributions, levies,
                withholdings, or liabilities wherever chargeable in any
                jurisdiction.
              </p>
              <p>
                <strong>“User”</strong> means any user on <strong>AllFans</strong>, whether a Creator or a Fan or
                both (also referred to as <strong>“you”</strong> or{" "}
                <strong>“your”</strong>).
              </p>
            </li>
            <li>
              <h5>Applicability</h5>
              <p>
                Each time a Fan/Creator Transaction is initiated on AllFans, 
                this agreement will apply to the exclusion of any other terms that the 
                Fan or Creator may propose, and this agreement legally binds the Fan and 
                Creator participating in the Fan/Creator Transaction.
              </p>
            </li>
            <li>
              <h5>Parties</h5>
              <p>
                The only parties to this agreement are the Fan and Creator participating 
                in the Fan/Creator Transaction. Innodev Solutions Inc. is not a party to 
                this agreement, and neither grants any rights for, nor participates in, 
                any Fan/Creator Transaction, except for acting as a payment intermediary 
                in accordance with the Creator’s and Fan’s instructions set out in section 
                4 of this agreement.
              </p>
            </li>
            <li>
              <h5>Pricing and Payment</h5>
              <p>
                By entering into a Fan/Creator Transaction, the Fan shall pay the Fan 
                Payment applicable to the relevant Fan/Creator Transaction in accordance 
                with the pricing published in the Creator’s account plus any Tax. The Fan 
                and Creator participating in the Fan/Creator Transaction hereby authorize 
                Innodev Solutions Inc. or any of its subsidiaries to act as a payment 
                intermediary and to collect, hold, and process the Fan Payment and any 
                Tax, to deduct the Platform Fee, and to pay out the sums due to Creators 
                and (if applicable) Referring Users, as described in 
                the <a href='/creatorterms' className='terms-link'>Creator Terms</a>.
              </p>
            </li>
            <li>
              <h5>Content License</h5>
              <p>
                Once the Fan has made the Fan Payment for a Fan/Creator Transaction, the 
                Creator hereby grants the Fan a limited license to access the applicable 
                Content of that Creator to which the Fan/Creator Transaction relates 
                (“Relevant Content”). This license is nontransferable, nonsublicensable, 
                and nonexclusive, and allows the Fan to access and view the Relevant 
                Content on the Fan’s personal device and through a normal web browser 
                (and to make a temporary copy of that Content only to the extent that this 
                is an incidental and technical process forming part of the Fan’s accessing 
                the Content (i.e., caching)), in accordance with the AllFans <a href='/acceptableuse' className='terms-link'>Acceptable Use Policy</a>.
              </p>
            </li>
            <li>
              <h5>Content Ownership</h5>
              <p>
                The Fan participating in the Fan/Creator Transaction acknowledges that 
                the Relevant Content license provided in section 5 of this agreement 
                does not result in the Fan acquiring any rights in or to the Relevant 
                Content, which rights will be retained by the Creator of the Relevant 
                Content.
              </p>
            </li>
            <li>
              <h5>
                License Termination
              </h5>
              <ol>
                <p>
                  The license granted to a Fan for the Relevant Content will terminate 
                  automatically without notice in the following circumstances:
                </p>
                <li>
                  <p>
                    if the Fan Payment related to the Fan/Creator Transaction 
                    was unsuccessful or is charged back or reversed for any reason;
                  </p>
                </li>
                <li>
                  <p>
                    as to pay-per-view Content, once the Fan has finished viewing the Content;
                  </p>
                </li>
                <li>
                  <p>
                    as to Subscriptions, at the end of the subscription period, unless the 
                    Subscription is configured to automatically renew as described in the <a href='/terms' className='terms-link'>Terms-of-Service Agreement</a>;
                  </p>
                </li>
                <li>
                  <p>if the Fan’s User account is suspended or terminated for any reason;</p>
                </li>
                <li>
                  <p>
                    if the Fan breaches the AllFans <a href='/acceptableuse' className='terms-link'>Acceptable Use Policy</a> (whether as to the Relevant Content or at all);
                  </p>
                </li>
                <li>
                  <p>
                    if the Content is removed from the Creator’s account; or
                  </p>
                </li>  
                <li>
                  <p>
                    if the Fan closes their AllFans User account.
                  </p>
                </li>
                <li>
                  <p>
                    By registering for an account, you hereby consent to receive
                    electronic communications from us about your account.
                    Communications might involve sending emails to the email
                    address you provided during registration or posting
                    communications on the Website and will include notices about
                    your account (e.g., change in password, confirmation emails,
                    and other transactional information) and are part of your
                    relationship with us. You acknowledge that any notices,
                    agreements, disclosures, or other communications that we
                    send you electronically will satisfy any legal communication
                    requirements, including that those communications be in
                    writing. We recommend that you keep copies of electronic
                    communications by printing a paper copy or saving an
                    electronic copy.{" "}
                  </p>
                  <p>
                    You also hereby consent to receive other communications from
                    us, including newsletters about new features and content,
                    special offers, promotional announcements, and customer
                    surveys by email or other methods. You acknowledge that
                    communications you receive from us might contain sexually
                    explicit material unsuitable for minors. If you no longer
                    want to receive non-transactional communications from us,
                    please review our <a href="/privacypolicy" className='terms-link'>Privacy Policy</a> on 
                    how to opt out of marketing communications.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <h5>Cancellation and Refunds</h5>
              <p>As to every Fan/Creator Transaction:</p>
              <ol>
                <li>
                  <p>
                    The Fan and the Creator participating in the Fan/Creator 
                    Transaction shall comply at all times with the AllFans 
                    <a href='/acceptableuse' className='terms-link'>Acceptable Use Policy</a> for the 
                    Relevant Content, including when accessing, viewing, and interacting with it.
                  </p>
                </li>
                <li>
                  <p>
                    This agreement does not affect any statutory right to 
                    receive a refund from the Creator that a Fan may have under law.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <h5>Creator and Fan Obligations</h5>
              <p>As to every Fan/Creator Transaction:</p>
              <ol>
                <li>
                  <p>
                    The Fan and the Creator participating in the Fan/Creator 
                    Transaction shall comply at all times with the AllFans 
                    <a href='/acceptableuse' className='terms-link'>Acceptable Use Policy</a> for 
                    the Relevant Content, including when accessing, viewing, and interacting with it.
                  </p>
                </li>
                <li>
                  <p>
                    The Fan participating in the Fan/Creator Transaction shall 
                    make the Fan Payment required to access, view, or interact with the 
                    Relevant Content, and shall not initiate a chargeback other than if 
                    the Fan disputes the Fan/Creator Transaction in good faith.
                  </p>
                </li>
                <li>
                  <p>
                    The Creator participating in the Fan/Creator Transaction shall 
                    make the Relevant Content available to the Fan once the Fan has made 
                    the Fan Payment applicable to the Relevant Content, including by 
                    producing any custom Content in accordance with the terms agreed to 
                    by the parties at checkout.
                  </p>
                </li>
                <li>
                  <p>
                    The Creator states that it possesses all necessary rights in and 
                    to the Relevant Content sufficient to license it to the Fan in the 
                    territory in which the Fan will be accessing or viewing the Relevant 
                    Content and has obtained all permissions and consents needed to grant 
                    the license in section 5 of this agreement.
                  </p>
                </li>
                <li>
                  <p>
                    The Creator is solely responsible for creating and uploading the Relevant 
                    Content. The Creator provides no guarantees that it will continue to create 
                    and upload Relevant Content on an ongoing basis.
                  </p>
                </li>
                <li>
                  <p>
                    Unless there has been negligence or other breach of duty by the Creator, the 
                    Fan’s accessing of the Creator’s Content is entirely at the Fan’s own risk.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <h5>No Guarantees</h5>
              <p>The Fan participating in the Fan/Creator Transaction acknowledges that Creators 
                may add and remove Content at any time from their Creator account, and that Creators 
                have the discretion to decide what sort of Content to make available on their account. 
                In addition, the Fan participating in the Fan/Creator Transaction acknowledges that 
                there might be circumstances where it is not possible for the Fan to access Relevant 
                Content to be provided under the Fan/Creator Transaction, including:
              </p>
              <ol>
                <li>
                  <p>
                    if the Creator’s account is suspended or deleted;
                  </p>
                </li>
                <li>
                  <p>
                    if the Fan’s account is suspended or deleted;
                  </p>
                </li>
                <li>
                  <p>
                    if the availability of any part of the AllFans website is suspended or inaccessible; or
                  </p>
                </li>
                <li>
                  <p>
                    if the Creator cannot create or upload Relevant Content in the future.
                  </p>
                </li>
              </ol>  
            </li>
            <li>
              <h5>Governing Law and Dispute Resolution</h5>
              <ol>
                <li>
                  <p>
                    Delaware law governs all adversarial proceedings arising out of the subject matter of this 
                    agreement. The Fan may also rely on mandatory rules of the law of the jurisdiction where the Fan resides.
                  </p>
                </li>
                <li>
                  <p>
                    As the exclusive means of initiating adversarial proceedings to resolve any dispute arising 
                    out of the subject matter of this agreement, a party may demand that the dispute be resolved by 
                    arbitration administered by Arbitration Resolution Services, Inc. (ARS) (or a similar online 
                    dispute resolution provider if ARS is not available) in accordance with its arbitration rules 
                    available at <a href='http://www.arbresolutions.com' className='terms-link'>www.arbresolutions.com</a>, and each party hereby consents to any such dispute being so 
                    resolved. Judgment on any award rendered in any such arbitration may be entered in any court having 
                    jurisdiction.
                  </p>
                </li>
                <li>
                  <p>
                    Any disputes not subject to arbitration must be brought in the courts of competent 
                    jurisdiction where the Fan resides.
                  </p>
                </li>
              </ol>
            </li>
          </ol>
          </div>
        </div>
          <div className="pf-modal__footer">
            <div className="pf-page__social">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-default"
                style={{
                  width: '100%',
                  maxWidth: '220px'
                }}
              >
                Close
              </button>
            </div>
          </div>
      </div>
    </div>, document.body
  )
}

export default FanCreatorContractModal
